import React, { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { v4 as uuid } from 'uuid';

import { GqlCustomNavigationItem, CustomNavigationItemType, useGetCollectionsQuery } from 'types/graphql';
import { Select, SmallLoader } from 'shared/components';
import { useStores } from 'src/hooks/use-stores';

import { ModalControllerProps } from './modal-controller';
import { BaseCustomNavigationModal } from './base-custom-navigation-modal';

export function AddCollectionModal({ editingItem, onClose, onSave }: ModalControllerProps): JSX.Element {
  const { id: dispensaryId } = useParams<{ id: string }>();
  const { apolloClient } = useStores();
  const [selectedCollectionId, setSelectedCollectionId] = useState(editingItem?.connectedId ?? '');
  const [label, setLabel] = useState(editingItem?.label ?? '');

  const { data, loading } = useGetCollectionsQuery({
    client: apolloClient,
    variables: { dispensaryId, customCategory: false },
  });

  const collections = useMemo(() => data?.getCollections ?? [], [data]);

  const isEditing = !!editingItem;
  const isValid = selectedCollectionId !== '' && label.trim() !== '';

  const handleCollectionChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    const collectionId = e.target.value;
    setSelectedCollectionId(collectionId);

    const collection = collections.find((c) => c.id === collectionId);
    if (collection && !label) {
      setLabel(collection.title);
    }
  };

  const handleSave = (): void => {
    if (!isValid) {
      return;
    }

    const item: GqlCustomNavigationItem = {
      connectedId: selectedCollectionId,
      id: editingItem?.id ?? uuid(),
      items: editingItem?.items ?? [],
      label: label.trim(),
      navigationType: CustomNavigationItemType.collection,
      position: editingItem?.position ?? 0,
    };

    onSave(item);
    onClose();
  };

  return (
    <BaseCustomNavigationModal
      copy='Connect to a product collection.'
      handleSave={handleSave}
      headerText='Collection'
      inputLabel={label}
      isEditing={isEditing}
      isOpen
      isValid={isValid}
      onClose={onClose}
      setLabel={setLabel}
    >
      {loading ? (
        <SmallLoader />
      ) : (
        <Select
          disabled={isEditing}
          fontSize={13}
          onChange={handleCollectionChange}
          value={selectedCollectionId}
          width='100%'
        >
          <option value='' disabled>
            Select a collection
          </option>
          {collections.map((collection) => (
            <option key={collection.id} value={collection.id}>
              {collection.title} ({collection.productCount} products)
            </option>
          ))}
        </Select>
      )}
    </BaseCustomNavigationModal>
  );
}
